import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";
import { useUser } from "../context/UserContext";

function labelAccountType(value) {
  return String(value || "internal") === "customer_portal" ? "Klantportaal" : "Intern account";
}

function labelMethod(method) {
  const labels = { passkey: "Passkey", totp: "Authenticator-app", recovery_code: "Recoverycodes" };
  return labels[method] || String(method || "");
}

export default function PersonalProfilePage() {
  const { updateUser } = useUser();
  const [profile, setProfile] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/profile");
      const nextProfile = data?.profile || null;
      setProfile(nextProfile);
      setDisplayName(String(nextProfile?.displayName || ""));
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Profiel ophalen mislukt.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  async function saveDisplayName(event) {
    event.preventDefault();
    const nextDisplayName = String(displayName || "").trim();
    if (!nextDisplayName) {
      setError("Vul een weergavenaam in.");
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");
    try {
      const { data } = await api.patch("/profile", { displayName: nextDisplayName });
      const nextProfile = data?.profile || null;
      setProfile(nextProfile);
      setDisplayName(String(nextProfile?.displayName || nextDisplayName));
      updateUser({ naam: nextProfile?.displayName || nextDisplayName, displayName: nextProfile?.displayName || nextDisplayName });
      setMessage("Weergavenaam opgeslagen.");
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Weergavenaam opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <main className="min-h-screen bg-slate-50 p-6 text-slate-700">Profiel laden...</main>;
  }

  if (!profile) {
    return <main className="min-h-screen bg-slate-50 p-6 text-rose-700">{error || "Profiel niet beschikbaar."}</main>;
  }

  const strongAuth = profile.strongAuth || {};
  const roles = Array.isArray(profile.roles) ? profile.roles : [];
  const methods = Array.isArray(strongAuth.availableAuthMethods) ? strongAuth.availableAuthMethods : [];

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-3xl space-y-4">
        <header>
          <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">Mijn account</p>
          <h1 className="text-2xl font-bold text-slate-900">Persoonlijk profiel</h1>
        </header>

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Persoonlijke gegevens</h2>
          <form className="mt-4 max-w-lg space-y-3" onSubmit={saveDisplayName}>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">Weergavenaam</span>
              <input
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                maxLength={200}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                autoComplete="name"
                required
              />
            </label>
            <p className="text-sm text-slate-500">E-mailadres en overige accountgegevens worden beheerd via de daarvoor bestemde processen.</p>
            {error ? <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p> : null}
            {message ? <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{message}</p> : null}
            <button type="submit" disabled={saving} className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-60">
              {saving ? "Opslaan..." : "Weergavenaam opslaan"}
            </button>
          </form>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Werkcontext</h2>
          <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
            <div><dt className="text-slate-500">Organisatie</dt><dd className="font-medium text-slate-900">{profile.tenantName || "-"}</dd></div>
            <div><dt className="text-slate-500">Accounttype</dt><dd className="font-medium text-slate-900">{labelAccountType(profile.accountType)}</dd></div>
            <div className="sm:col-span-2"><dt className="text-slate-500">Rollen</dt><dd className="font-medium text-slate-900">{roles.length ? roles.join(", ") : (profile.role || "Geen interne rol")}</dd></div>
          </dl>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Beveiliging</h2>
          <p className="mt-2 text-sm text-slate-700">
            {strongAuth.strongAuthEnrolled ? "Sterke authenticatie is ingesteld." : "Sterke authenticatie is nog niet ingesteld."}
          </p>
          <p className="mt-1 text-sm text-slate-600">
            Methoden: {methods.length ? methods.map(labelMethod).join(", ") : "nog geen"}.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/mfa/setup?returnTo=%2Fprofiel" className="rounded-lg border border-blue-700 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50">Passkey instellen</Link>
            <Link to="/mfa/setup?returnTo=%2Fprofiel" className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Authenticator-app instellen</Link>
          </div>
        </section>
      </div>
    </main>
  );
}
